// client.ts — typed fetch wrapper around the PharmacyIQ API.
//
// - Base URL comes from VITE_API_BASE_URL (never hard-coded).
// - The auth token is injected via a pluggable provider that the MSAL layer
//   wires up at startup (see src/auth). Until then it returns null and requests
//   go out unauthenticated (fine against MSW mocks).
// - The active store is passed by callers as the `store` query param (which the
//   real API expects as `store_id`); there is one base URL.

import { refreshAccessToken } from '@/auth/devAuth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

type TokenProvider = () => Promise<string | null> | string | null;

let tokenProvider: TokenProvider = () => null;

/** Called by the auth layer to supply access tokens to outgoing requests. */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/** Get the current auth token (for raw fetch calls like FormData uploads). */
export async function getAuthToken(): Promise<string | null> {
  return tokenProvider();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public url: string,
    public body?: unknown,
  ) {
    super(`API ${status} for ${url}`);
    this.name = 'ApiError';
  }
}

/** Human-readable message for a failed request: the backend's `{detail}` when
 * present, a status fallback for other ApiErrors. Local onError handlers and
 * the global MutationCache toast both use this, so the toast layer can collapse
 * the pair into a single notification. */
export function apiErrorDetail(e: unknown, fallback = 'Something went wrong'): string {
  if (e instanceof ApiError) {
    if (e.body && typeof e.body === 'object' && 'detail' in e.body) {
      const d = (e.body as { detail?: unknown }).detail;
      if (typeof d === 'string' && d) return d;
    }
    return `Request failed (${e.status})`;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Query string params; `store` defaults to the active store. */
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, body, headers, ...rest } = options;

  const url = new URL(
    path.replace(/^\//, ''),
    BASE_URL ? BASE_URL.replace(/\/?$/, '/') : window.location.origin,
  );
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const token = await tokenProvider();
  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  if (body !== undefined) finalHeaders.set('Content-Type', 'application/json');

  let res = await fetch(url.toString(), {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // On 401, attempt token refresh before giving up
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      finalHeaders.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(url.toString(), {
        ...rest,
        headers: finalHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    }
    if (res.status === 401) {
      window.location.href = '/login';
      throw new ApiError(res.status, url.toString());
    }
  }

  if (!res.ok) {
    let errBody: unknown;
    try {
      errBody = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, url.toString(), errBody);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** DRF page envelope (the mock handlers return bare arrays instead). */
interface ListPage<T> {
  count?: number;
  next?: string | null;
  results?: T[];
}

const MAX_LIST_PAGES = 20;

export interface ListOptions extends RequestOptions {
  /**
   * Page-follow ceiling. Default MAX_LIST_PAGES suits operational lists
   * (batches, transfers, audit trail) that are bounded in practice. Set 1
   * for reference catalogues where truncation is the intended behaviour
   * (e.g. the 17k-row product directory feeding pickers) — capping there is
   * deliberate, so no warning is logged when maxPages is explicit.
   */
  maxPages?: number;
}

/**
 * Fetch a collection endpoint without silently truncating at page 1: DRF
 * paginates everything, so follow `page=N` until `next` is null. Mock
 * handlers return bare arrays — passed through untouched. Bounded at
 * maxPages so a runaway table degrades loudly (console warning) instead of
 * hanging the UI.
 */
export async function apiFetchList<T = unknown>(
  path: string,
  options: ListOptions = {},
): Promise<T[]> {
  const { maxPages, ...requestOptions } = options;
  const limit = maxPages ?? MAX_LIST_PAGES;
  const first = await apiFetch<unknown>(path, requestOptions);
  if (Array.isArray(first)) return first as T[];
  const page = first as ListPage<T>;
  if (!page || !Array.isArray(page.results)) return [];

  const all = [...page.results];
  let next = page.next ?? null;
  let pageNo = 2;
  while (next && pageNo <= limit) {
    const data = await apiFetch<ListPage<T>>(path, {
      ...requestOptions,
      query: { ...requestOptions.query, page: pageNo },
    });
    all.push(...(data.results ?? []));
    next = data.next ?? null;
    pageNo += 1;
  }
  if (next && maxPages === undefined) {
    console.warn(
      `apiFetchList: capped at ${limit} pages for ${path} ` +
        `(${all.length} of ${page.count ?? '?'} rows loaded)`,
    );
  }
  return all;
}
